import { computed, onMounted, onScopeDispose, shallowRef, toValue, watch } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { createElementConnection, observeHostConnections } from "#ce/shared/element-connection";
import { resolveFinder } from "#ce/shared/finder-association";

/** Subscribes to the enclosing page finder and reads its kind definition. Search params and filter values remain undefined until publication. */
export function useFinder({ finder } = {}) {
  const host = useHostElement();
  const snapshot = shallowRef();
  const element = shallowRef();
  const kind = shallowRef();
  const connected = shallowRef(false);
  const associationError = shallowRef();
  let stop;
  const connection = createElementConnection({
    resolve: () => resolveFinder(host, toValue(finder)),
    connect: subscribe,
    changed: updateAssociation,
  });

  function receive(value) {
    snapshot.value = value;
  }

  function subscribe(finderElement) {
    element.value = finderElement;
    connected.value = true;
    kind.value = finderElement.kindDefinition;

    const handle = finderElement.subscribe(receive);

    return {
      get closed() {
        return handle.closed;
      },

      disconnect() {
        handle.disconnect();
        connected.value = false;
        snapshot.value = undefined;
        element.value = undefined;
        kind.value = undefined;
      },
    };
  }

  function updateAssociation({ error, ready }) {
    associationError.value = error;

    if (!ready) {
      connected.value = false;
      snapshot.value = undefined;
      element.value = undefined;
      kind.value = undefined;
    }
  }

  function start() {
    if (host?.ownerDocument) {
      stop = observeHostConnections(host, connection.reconcile, ["finder"]);
    }

    connection.reconcile();
  }

  function dispose() {
    stop?.();
    connection.disconnect();
  }

  watch(() => toValue(finder), connection.reconcile);
  onMounted(start);
  onScopeDispose(dispose);

  const fields = Object.fromEntries(
    [
      "params",
      "filterValues",
      "presentation",
      "transientLocation",
      "revision",
      "publication",
      "request",
      "completed",
      "error",
      "attached",
      "route",
      "settings",
      "mapSelection",
      "mapBoundsMode",
      "landingDraft",
      "landingDraftTransientLocation",
    ].map((key) => [key, computed(() => snapshot.value?.[key])]),
  );

  return {
    ...fields,
    referringUrl: computed(() =>
      snapshot.value ? element.value?.referringUrl : resolveFinder(host, toValue(finder)).element?.referringUrl,
    ),
    kind: computed(() => kind.value),
    connected: computed(() => connected.value),
    associationError: computed(() => associationError.value),
  };
}
